/**
 * Browse-Form Template: Coordinated browse → select → form flow
 *
 * This template solves the multi-block problem by handling the entire
 * "browse items, select some, fill out a form" flow in a single component.
 *
 * User flow:
 * 1. Browse items with filters
 * 2. Select one or more items
 * 3. Form section appears
 * 4. Fill out form and submit
 * 5. Single action emitted with selections + form data
 */

import { registerBlockRenderer, getBlockRendererContext, limitArray } from '../registry';
import { inlineLoaderHTML } from '../../../core/loading/activity-loader';
import { escapeHtml, createBlockWrapper, createErrorState, createEmptyState } from '../utils';
import { iconChevronDown } from '../../icons';
import type { BrowseFormData, TemplateFormField } from './types';
import type { LiveDataItem, LiveSectionResponse } from '../../../core/api-client';

// =============================================================================
// BROWSE-FORM TEMPLATE
// =============================================================================

registerBlockRenderer('browse-form', (data: unknown, container: HTMLElement, onAction?: (value: string) => void) => {
  const templateData = data as BrowseFormData;
  const ctx = getBlockRendererContext();

  if (!templateData.section) {
    console.warn('[browse-form] Missing required "section" field');
    const wrapper = createBlockWrapper('browse-form');
    wrapper.innerHTML = createErrorState('This content is not available');
    container.appendChild(wrapper);
    return;
  }

  // Create main wrapper
  const wrapper = createBlockWrapper('browse-form');
  wrapper.classList.add('pms-template-browse-form');

  // Internal state
  const state = {
    phase: 'browse' as 'browse' | 'form' | 'submitted',
    selectedItems: new Map<string, LiveDataItem>(),
    formValues: {} as Record<string, string>,
    items: [] as LiveDataItem[],
    filters: [] as { id: string; label: string; options: string[] }[],
    activeFilters: new Map<string, string>(),
  };

  // Loading state
  wrapper.innerHTML = `
    <div class="pms-template-loading">
      ${inlineLoaderHTML('Loading...')}
    </div>
  `;
  container.appendChild(wrapper);

  // Fetch data and render
  loadAndRender();

  async function loadAndRender(): Promise<void> {
    if (!ctx.apiClient || !ctx.workspaceId) {
      wrapper.innerHTML = createErrorState('This content is not available right now');
      return;
    }

    try {
      const apiClient = ctx.apiClient as {
        getLiveSectionData: (workspaceId: string, sectionId: string, filters: Record<string, string>, limit: number) => Promise<LiveSectionResponse>
      };

      const response = await apiClient.getLiveSectionData(
        ctx.workspaceId,
        templateData.section,
        templateData.initialFilters || {},
        50
      );

      state.items = limitArray(response.items, 50);
      state.filters = limitArray(response.filters || [], 10).map(f => ({
        id: f.id,
        label: f.label,
        options: limitArray(f.options.map(o => o.value), 50)
      }));

      // Initialize active filters from initial filters
      if (templateData.initialFilters) {
        for (const [key, value] of Object.entries(templateData.initialFilters)) {
          state.activeFilters.set(key, value);
        }
      }

      // Clear loading and render
      wrapper.innerHTML = '';
      renderTemplate();

    } catch (error) {
      console.error('[browse-form] Failed to load data:', error);
      wrapper.innerHTML = createErrorState('This content could not be loaded right now');
    }
  }

  function renderTemplate(): void {
    const title = templateData.title || 'Select Items';

    // Header
    const header = document.createElement('div');
    header.className = 'pms-template-header';
    header.innerHTML = `
      <h3 class="pms-template-title">${escapeHtml(title)}</h3>
      <span class="pms-template-count">${state.items.length} items</span>
      <span class="pms-template-selection" style="display: none;"></span>
    `;
    wrapper.appendChild(header);

    // Filters
    if (state.filters.length > 0) {
      const filtersEl = renderFilters();
      wrapper.appendChild(filtersEl);
    }

    // Browse grid
    const grid = document.createElement('div');
    grid.className = 'pms-template-grid';
    renderItems(grid);
    wrapper.appendChild(grid);

    // Form section (hidden until selections made)
    const formSection = document.createElement('div');
    formSection.className = 'pms-template-form-section';
    formSection.style.display = 'none';
    renderFormSection(formSection);
    wrapper.appendChild(formSection);

    // Update selection display
    updateSelectionUI();
  }

  function renderFilters(): HTMLElement {
    const filtersWrapper = document.createElement('div');
    filtersWrapper.className = 'pms-browse-filters';

    for (const filter of state.filters) {
      const filterGroup = document.createElement('div');
      filterGroup.className = 'pms-browse-filter-item';

      const label = document.createElement('label');
      label.className = 'pms-browse-filter-label';
      label.textContent = filter.label;
      filterGroup.appendChild(label);

      const selectWrapper = document.createElement('div');
      selectWrapper.className = 'pms-browse-select-wrapper';

      const select = document.createElement('select');
      select.className = 'pms-browse-select';
      select.dataset['filterId'] = filter.id;

      const allOption = document.createElement('option');
      allOption.value = '';
      allOption.textContent = 'All';
      select.appendChild(allOption);

      for (const option of filter.options) {
        const opt = document.createElement('option');
        opt.value = option;
        opt.textContent = option;
        opt.selected = state.activeFilters.get(filter.id)?.toLowerCase() === option.toLowerCase();
        select.appendChild(opt);
      }

      select.addEventListener('change', () => {
        state.activeFilters.set(filter.id, select.value);
        applyFilters();
      });

      const chevron = document.createElement('span');
      chevron.className = 'pms-browse-select-chevron';
      chevron.innerHTML = iconChevronDown();

      selectWrapper.appendChild(select);
      selectWrapper.appendChild(chevron);
      filterGroup.appendChild(selectWrapper);
      filtersWrapper.appendChild(filterGroup);
    }

    return filtersWrapper;
  }

  function renderItems(grid: HTMLElement): void {
    grid.innerHTML = '';

    if (state.items.length === 0) {
      grid.innerHTML = createEmptyState('No items found', { compact: true });
      return;
    }

    for (const item of state.items) {
      const card = renderItemCard(item);
      grid.appendChild(card);
    }
  }

  function renderItemCard(item: LiveDataItem): HTMLElement {
    const card = document.createElement('div');
    card.className = 'pms-template-card';
    card.dataset['itemId'] = item.id;
    card.dataset['tags'] = [item.category, item.subcategory].filter(Boolean).join(',').toLowerCase();

    // Store filter metadata
    if (item.metadata) {
      for (const [key, value] of Object.entries(item.metadata)) {
        if (value !== undefined && value !== null) {
          card.dataset['filter' + key.charAt(0).toUpperCase() + key.slice(1).toLowerCase()] = String(value).toLowerCase();
        }
      }
    }

    // Selection checkbox
    const checkbox = document.createElement('div');
    checkbox.className = 'pms-template-card-checkbox';
    checkbox.innerHTML = `
      <label class="pms-browse-checkbox-wrapper">
        <input type="checkbox" class="pms-browse-checkbox" />
        <span class="pms-browse-checkbox-mark"></span>
      </label>
    `;
    card.appendChild(checkbox);

    // Image
    if (item.imageUrl) {
      const img = document.createElement('img');
      img.className = 'pms-template-card-image';
      img.src = item.imageUrl;
      img.alt = item.title;
      img.loading = 'lazy';
      img.onerror = () => img.style.display = 'none';
      card.appendChild(img);
    }

    // Content
    const content = document.createElement('div');
    content.className = 'pms-template-card-content';

    if (item.badge) {
      content.innerHTML += `<span class="pms-template-card-badge">${escapeHtml(item.badge)}</span>`;
    }
    content.innerHTML += `<h4 class="pms-template-card-title">${escapeHtml(item.title)}</h4>`;
    if (item.description) {
      content.innerHTML += `<p class="pms-template-card-desc">${escapeHtml(item.description)}</p>`;
    }
    if (item.price) {
      content.innerHTML += `<span class="pms-template-card-price">${escapeHtml(item.price)}</span>`;
    }

    card.appendChild(content);

    // Click handler
    const input = checkbox.querySelector('input') as HTMLInputElement;
    card.addEventListener('click', (e) => {
      if ((e.target as HTMLElement).closest('a')) return;

      const isMulti = templateData.selectMode !== 'single';

      if (!isMulti) {
        // Single select - clear others
        state.selectedItems.clear();
        wrapper.querySelectorAll('.pms-template-card').forEach(c => {
          c.classList.remove('pms-template-card-selected');
          (c.querySelector('input') as HTMLInputElement).checked = false;
        });
      }

      if (state.selectedItems.has(item.id)) {
        state.selectedItems.delete(item.id);
        card.classList.remove('pms-template-card-selected');
        input.checked = false;
      } else {
        state.selectedItems.set(item.id, item);
        card.classList.add('pms-template-card-selected');
        input.checked = true;
      }

      updateSelectionUI();
    });

    return card;
  }

  function renderFormSection(formSection: HTMLElement): void {
    const prompt = templateData.formPrompt || 'Complete the form below';

    formSection.innerHTML = `
      <div class="pms-template-form-header">
        <h4>${escapeHtml(prompt)}</h4>
        <span class="pms-template-form-selected-summary"></span>
      </div>
      <form class="pms-template-form">
        <div class="pms-template-form-fields"></div>
        <div class="pms-template-form-actions">
          <button type="button" class="pms-template-btn-back">Back to Selection</button>
          <button type="submit" class="pms-template-btn-submit">${escapeHtml(templateData.submitLabel || 'Submit')}</button>
        </div>
      </form>
    `;

    // Render form fields
    const fieldsContainer = formSection.querySelector('.pms-template-form-fields') as HTMLElement;
    for (const field of templateData.formFields) {
      const fieldEl = renderFormField(field);
      fieldsContainer.appendChild(fieldEl);
    }

    // Back button
    const backBtn = formSection.querySelector('.pms-template-btn-back') as HTMLButtonElement;
    backBtn.addEventListener('click', () => {
      state.phase = 'browse';
      formSection.style.display = 'none';
      (wrapper.querySelector('.pms-template-grid') as HTMLElement).style.display = '';
      const filtersEl = wrapper.querySelector('.pms-browse-filters') as HTMLElement;
      if (filtersEl) filtersEl.style.display = '';
    });

    // Submit handler
    const form = formSection.querySelector('form') as HTMLFormElement;
    form.addEventListener('submit', (e) => {
      e.preventDefault();
      handleSubmit();
    });
  }

  function renderFormField(field: TemplateFormField): HTMLElement {
    const fieldWrapper = document.createElement('div');
    fieldWrapper.className = 'pms-template-field';

    const label = document.createElement('label');
    label.className = 'pms-template-field-label';
    label.textContent = field.label;
    if (field.required) {
      label.innerHTML += ' <span class="pms-template-required">*</span>';
    }
    fieldWrapper.appendChild(label);

    let input: HTMLElement;

    switch (field.type) {
      case 'textarea':
        input = document.createElement('textarea');
        (input as HTMLTextAreaElement).placeholder = field.placeholder || '';
        (input as HTMLTextAreaElement).required = field.required || false;
        input.className = 'pms-template-input pms-template-textarea';
        break;

      case 'select':
        input = document.createElement('select');
        input.className = 'pms-template-input pms-template-select';
        const defaultOpt = document.createElement('option');
        defaultOpt.value = '';
        defaultOpt.textContent = field.placeholder || 'Select...';
        input.appendChild(defaultOpt);
        for (const opt of (field.options || [])) {
          const option = document.createElement('option');
          if (typeof opt === 'string') {
            option.value = opt;
            option.textContent = opt;
          } else {
            option.value = opt.value;
            option.textContent = opt.label;
          }
          input.appendChild(option);
        }
        break;

      case 'slider':
        const sliderWrapper = document.createElement('div');
        sliderWrapper.className = 'pms-template-slider-wrapper';

        const slider = document.createElement('input');
        slider.type = 'range';
        slider.className = 'pms-template-slider';
        slider.min = String(field.slider?.min ?? 0);
        slider.max = String(field.slider?.max ?? 100);
        slider.step = String(field.slider?.step ?? 1);
        slider.value = String(field.slider?.defaultValue ?? field.slider?.min ?? 0);

        const sliderValue = document.createElement('span');
        sliderValue.className = 'pms-template-slider-value';
        const prefix = field.slider?.prefix || '';
        const suffix = field.slider?.suffix || '';
        sliderValue.textContent = `${prefix}${slider.value}${suffix}`;

        slider.addEventListener('input', () => {
          sliderValue.textContent = `${prefix}${slider.value}${suffix}`;
          state.formValues[field.id] = slider.value;
        });

        sliderWrapper.appendChild(slider);
        sliderWrapper.appendChild(sliderValue);
        input = sliderWrapper;
        state.formValues[field.id] = slider.value;
        break;

      default:
        input = document.createElement('input');
        (input as HTMLInputElement).type = field.type || 'text';
        (input as HTMLInputElement).placeholder = field.placeholder || '';
        (input as HTMLInputElement).required = field.required || false;
        input.className = 'pms-template-input';
    }

    if (input.tagName !== 'DIV') {
      input.dataset['fieldId'] = field.id;
      input.addEventListener('input', (e) => {
        state.formValues[field.id] = (e.target as HTMLInputElement).value;
      });
    }

    fieldWrapper.appendChild(input);
    return fieldWrapper;
  }

  function updateSelectionUI(): void {
    const count = state.selectedItems.size;
    const selectionEl = wrapper.querySelector('.pms-template-selection') as HTMLElement;
    const formSection = wrapper.querySelector('.pms-template-form-section') as HTMLElement;
    const grid = wrapper.querySelector('.pms-template-grid') as HTMLElement;
    const filtersEl = wrapper.querySelector('.pms-browse-filters') as HTMLElement;

    if (selectionEl) {
      if (count > 0) {
        selectionEl.textContent = `${count} selected`;
        selectionEl.style.display = '';
      } else {
        selectionEl.style.display = 'none';
      }
    }

    // Show/hide form section based on selection
    if (count > 0 && state.phase !== 'submitted') {
      state.phase = 'form';
      formSection.style.display = '';
      grid.style.display = 'none';
      if (filtersEl) filtersEl.style.display = 'none';

      // Update selected summary in form
      const summary = formSection.querySelector('.pms-template-form-selected-summary') as HTMLElement;
      if (summary) {
        const names = Array.from(state.selectedItems.values()).map(i => i.title).join(', ');
        summary.textContent = `Selected: ${names}`;
      }
    } else if (count === 0) {
      state.phase = 'browse';
      formSection.style.display = 'none';
      grid.style.display = '';
      if (filtersEl) filtersEl.style.display = '';
    }
  }

  function applyFilters(): void {
    const cards = wrapper.querySelectorAll('.pms-template-card');
    let visibleCount = 0;

    cards.forEach(cardEl => {
      const card = cardEl as HTMLElement;
      let visible = true;

      for (const [filterId, selectedValue] of state.activeFilters.entries()) {
        if (!selectedValue) continue;

        const camelKey = 'filter' + filterId.charAt(0).toUpperCase() + filterId.slice(1).toLowerCase();
        const cardValue = card.dataset[camelKey] || '';
        const cardTags = card.dataset['tags'] || '';

        const lowerVal = selectedValue.toLowerCase();
        if (!cardValue.includes(lowerVal) && !cardTags.includes(lowerVal)) {
          visible = false;
          break;
        }
      }

      card.style.display = visible ? '' : 'none';
      if (visible) visibleCount++;
    });

    // Update count
    const countEl = wrapper.querySelector('.pms-template-count');
    if (countEl) {
      countEl.textContent = `${visibleCount} items`;
    }
  }

  function handleSubmit(): void {
    // Collect selected IDs
    const selectedIds = Array.from(state.selectedItems.keys());

    // Collect form values
    const formFields: string[] = [];
    for (const [key, value] of Object.entries(state.formValues)) {
      if (value) {
        formFields.push(`${key}=${encodeURIComponent(value)}`);
      }
    }

    // Build action string
    // Format: submitAction:section:ids=1,2,3;field1=value1;field2=value2
    const actionParts = [
      templateData.submitAction,
      templateData.section,
      `ids=${selectedIds.join(',')}`,
      ...formFields
    ];

    const actionString = `${actionParts[0]}:${actionParts[1]}:${actionParts.slice(2).join(';')}`;

    state.phase = 'submitted';

    // Show success state
    const formSection = wrapper.querySelector('.pms-template-form-section') as HTMLElement;
    formSection.innerHTML = `
      <div class="pms-template-success">
        <div class="pms-template-success-icon">&#10003;</div>
        <h4>Request Submitted</h4>
        <p>We've received your request for ${state.selectedItems.size} item(s).</p>
      </div>
    `;

    // Emit action
    if (onAction) {
      onAction(actionString);
    }
  }
});
